/**
 * Shared builder: PL `FeaturedProductsShowcaseWidgetData` (the "featured products swimlane") →
 * domain `CustomSection`.
 *
 * Mirrors `banner-section-builders.ts`: dependency-light, no JSX. The FEATURED_PRODUCTS_SHOWCASE
 * container widget uses it for the section-level proto→domain conversion (heading, CTA buttons,
 * device caps, analytics); product cards render as pre-rendered SINGLE_PRODUCT_CARD children, so
 * the container passes `[]` for them.
 */

import type { IProductCard } from '@src/components/product-card/product-card.types'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'

import { makeSection } from '../banner-section-builders'
import { buildWidgetTrackingMetaData, buildWidgetTrackingTypes, protoButtonToDomain, toRichText } from '../widget-utils'

import type { FeaturedProductsShowcaseWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export function buildFeaturedProductsShowcaseConfig(
  data: FeaturedProductsShowcaseWidgetData,
  productCards: IProductCard[],
  tracker?: string,
  sectionPosition?: number,
): CustomSection {
  const heading = toRichText(data.heading as Parameters<typeof toRichText>[0])
  return makeSection({
    id: data.id,
    // The Contentful section type behind this widget is BACK_TO_SCHOOL (the proto pins it) — keep
    // it so `context.sectionType` in card click/wishlist events matches the non-PL flow exactly.
    sectionType: data.sectionType as CUSTOM_SECTION_TYPE,
    richTexts: (heading ? [heading] : []) as CustomSection['richTexts'],
    buttons: data.buttons.map(protoButtonToDomain) as CustomSection['buttons'],
    productCards,
    numberOfProductsToShowInDesktop: data.numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile: data.numberOfProductsToShowInMobile,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics, sectionPosition),
    // Arms the PL section-level IS_VISIBLE/CLICK emitter (PLSectionTrackingWrapper) once the wire
    // ships trackingTypes — legacy sends ['VISIBLE'] for every home section (proto 3.20.0).
    trackingTypes: buildWidgetTrackingTypes(data.analytics),
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    mobileDynamicClassesOverrides: data.mobileDynamicClassesOverrides,
    // Mirrors non-PL `SectionRenderer.tsx`'s unconditional `tracker: params.tracker` injection —
    // without it, every click/impression tracker string on this widget bakes in the literal
    // "undefined" (see STRFRNT home-PL analytics parity fix).
    tracker,
    // Derived 1-based page position — legacy bakes it into the section payload as both
    // `elementPosition` and `context.contentContainerPosition` (the PL wire carries neither).
    elementPosition: sectionPosition,
  })
}
